import { createTheme } from "@mui/material/styles";

/** Temas de marca TIP, los mismos que TIPControl: claro (lateral negro) y oscuro. */
const THEMES = {
	light: {
		brand: "#F2C702",
		brandContrast: "#141414",
		accent: "#1A1A17",
		secondary: "#1F2937",
		background: "#F6F5F1",
		surface: "#FFFFFF",
		surface2: "#F1EFE8",
		sider: "#141414",
		text: "#1A1A17",
		textSecondary: "#57554E",
		textMuted: "#8C8A82",
		border: "#E6E3DA",
		hover: "rgba(242, 199, 2, 0.14)",
		active: "rgba(242, 199, 2, 0.26)",
		success: "#16a34a",
		warning: "#d97706",
		error: "#dc2626",
		info: "#0284c7",
		shadow: "0 1px 2px rgba(16,24,40,0.05), 0 2px 8px rgba(16,24,40,0.06)",
		shadowLg: "0 16px 40px rgba(16,24,40,0.12)",
	},
	dark: {
		brand: "#F2C702",
		brandContrast: "#111111",
		accent: "#F2C702",
		secondary: "#E5E7EB",
		background: "#121212",
		surface: "#1C1C1B",
		surface2: "#242422",
		sider: "#171716",
		text: "#F2F0EA",
		textSecondary: "#B9B6AC",
		textMuted: "#85827A",
		border: "#2E2D2A",
		hover: "rgba(242, 199, 2, 0.10)",
		active: "rgba(242, 199, 2, 0.20)",
		success: "#22c55e",
		warning: "#f59e0b",
		error: "#f87171",
		info: "#38bdf8",
		shadow: "0 1px 2px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.25)",
		shadowLg: "0 16px 40px rgba(0,0,0,0.5)",
	},
};

/** Aplica el tema al documento: variables CSS, clase del modo oscuro y color de la barra del móvil. */
export const applyThemeVars = (isLight) => {
	const t = isLight ? THEMES.light : THEMES.dark;
	const vars = {
		"--sider-background": t.sider,
		"--sider-text": "#F3F1EA",
		"--sider-muted": "rgba(243,241,234,0.52)",
		"--sider-icon": "rgba(243,241,234,0.78)",
		"--sider-hover": "rgba(255,255,255,0.07)",
		"--sider-border": "rgba(255,255,255,0.09)",
		"--brand-color": t.brand,
		"--brand-contrast": t.brandContrast,
		"--accent-color": t.accent,
		"--success-color": t.success,
		"--warning-color": t.warning,
		"--error-color": t.error,
		"--info-color": t.info,
		"--background-color": t.background,
		"--card-background": t.surface,
		"--surface-2": t.surface2,
		"--text-color": t.text,
		"--text-secondary": t.textSecondary,
		"--text-muted": t.textMuted,
		"--border-color": t.border,
		"--hover-color": t.hover,
		"--active-color": t.active,
		"--box-shadow": t.shadow,
		"--box-shadow-lg": t.shadowLg,
	};
	Object.entries(vars).forEach(([name, value]) => document.documentElement.style.setProperty(name, value));
	document.body.classList.toggle("dark-theme", !isLight);
	document.documentElement.style.colorScheme = isLight ? "light" : "dark";
	document.querySelector('meta[name="theme-color"]')?.setAttribute("content", t.sider);
};

export const buildAppTheme = (isLight) => {
	const t = isLight ? THEMES.light : THEMES.dark;
	const display = "'Charlie Display', 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif";

	return createTheme({
		palette: {
			mode: isLight ? "light" : "dark",
			tonalOffset: 0.1,
			primary: { main: t.brand, contrastText: t.brandContrast },
			secondary: { main: t.secondary },
			error: { main: t.error },
			warning: { main: t.warning },
			success: { main: t.success },
			info: { main: t.info },
			background: { default: t.background, paper: t.surface },
			text: { primary: t.text, secondary: t.textSecondary, disabled: t.textMuted },
			divider: t.border,
		},
		shape: { borderRadius: 6 },
		typography: {
			fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
			fontSize: 14,
			h1: { fontFamily: display },
			h2: { fontFamily: display },
			h3: { fontFamily: display },
			h4: { fontFamily: display },
			h5: { fontFamily: display },
			h6: { fontFamily: display },
			button: { fontWeight: 700 },
		},
		components: {
			MuiAutocomplete: {
				defaultProps: { noOptionsText: "Sin resultados", loadingText: "Buscando…", clearText: "Borrar", openText: "Abrir", closeText: "Cerrar" },
			},
			MuiButton: {
				defaultProps: { disableElevation: true },
				styleOverrides: {
					root: {
						textTransform: "none",
						borderRadius: 10,
						fontWeight: 700,
						// Con la marca amarilla, los botones sin relleno usan el acento legible.
						variants: [
							{ props: { variant: "outlined", color: "primary" }, style: { color: t.accent, borderColor: t.accent } },
							{ props: { variant: "text", color: "primary" }, style: { color: t.accent } },
						],
					},
				},
			},
			MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
			MuiDialogTitle: { styleOverrides: { root: { fontWeight: 800 } } },
			MuiLink: { styleOverrides: { root: { color: t.accent } } },
			MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10 } } },
			MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
			MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, "&.Mui-selected": { color: t.accent } } } },
			MuiTabs: { styleOverrides: { indicator: { backgroundColor: t.brand, height: 3 } } },
			// La etiqueta enfocada, en color de texto: el amarillo no se lee sobre fondo claro.
			MuiInputLabel: { styleOverrides: { root: { "&.Mui-focused": { color: t.accent } } } },
		},
	});
};
